import Box from "@mui/material/Box";
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { CustomStyleFormHelperText, CustomStyleSuffixText, CustomStyleTextArea, CustomStyleTextField } from "./Styles";
import CustomTextFieldApp from "./CustomTextField/CustomTextFieldApp";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * @Description Input field (text, number)
 * @param String id: Identifier for the input field (required)
 * @param Integer length: Maximum length for the value in the text field (optional)
 * @param String counterColor: Color value for the counter legend (optional)
 * @param String name: Name for the input field (optional)
 * @param Boolean emptyFields: Indicates if the field is empty (optional)
 * @param Boolean isValid: Show legend and input with error when isValid = false (optional)
 * @param String isValidText: Legend for input with error (optional)
 * @param String value: Value for show in input field (required)
 * @param Boolean disabled: Enable/disable writing in the input field. A disabled field displays its whole value, because it cannot be scrolled (optional)
 * @param String trans: Text for the label (required)
 * @param String type: Defines the input type: text/number (required)
 * @param String placeholder: Defines the placeholder text for the input field (optional)
 * @param Object inputProps: Defines attributes to apply to the native input field (optional)
 * @param JSX.Element InputProps: Defines an element (InputAdornment) to add to the input field (optional)
 * @param String backgroundColor: Background color value for the input field (optional)
 * @param String fontColor: Font color value for the input field (optional)
 * @param String suffixText: Text to show as an InputAdornment (optional)
 * @param Boolean countDecimals: If the text includes decimals, count them in the counter (optional)
 * @param Boolean standard: If standard == true, only show the bottom border in the input field (optional)
 * @param Boolean required: Input field is required (optional)
 * @param Boolean multiline: Is a textarea (optional)
 * @param Integer rows: When multiline, show these default lines in the input field (optional)
 * @param Integer maxRows: When multiline, show this maximum number of lines in the input field (optional)
 * @param String height: Defines the height for the input field (optional)
 * @param Boolean showCounter: If showCounter == true, count characters in the value and show them under the input field (optional)
 * @param String helperText: Text for the legend, displayed under the input field (optional)
 * @param Event onFocus: onFocus trigger in the input field (optional)
 * @param Event onBlur: onBlur trigger in the input field (optional)
 * @param Event onChange: onChange trigger in the input field (optional)
 * @param Event onKeyDown: onKeyDown trigger in the input field (optional)
 * @param Boolean showHelperTextAlways: If true, displays the helperText immediately below the input field
 *                                     regardless of the field's value or validation state.
 *                                     This bypasses the default conditional logic that only shows
 *                                     helperText when the field is empty and emptyFields is active.
 *                                     Default: false
 * @returns {JSX.Element}
 */

export const CustomTextField = (props) => {
    let {
        id,
        length = 60,
        counterColor = '#707070',
        name,
        emptyFields,
        isValid = true,
        isValidText = 'format_invalid',
        value,
        disabled = false,
        trans,
        type = "text",
        placeholder = '',
        inputProps = {},
        InputProps = false,
        backgroundColor = "transparent",
        fontColor = "",
        suffixText = '',
        countDecimals = false,
        decimals = null,
        standard = false,
        required = false,
        multiline = false,
        rows = 2,
        maxRows = 4,
        height = '',
        showCounter = true,
        helperText = '',
        showHelperTextAlways=false,
        onFocus,
        onBlur,
        onChange,
        onKeyDown,
        autoComplete = 'new-password',
        readOnly,
        click=null,
        marginBottom=true,
    } = props;

    const {
        t,
        style,
        customInputProps,
        lCode,
        valueEmpty,
        handleKeyDown,
        handleNumberInputChange,
        handleMultilineChange,
        handlePaste,
        getSentLength,
        CharacterCount
    } = CustomTextFieldApp({
        type,
        length,
        height,
        backgroundColor,
        fontColor,
        inputProps,
        countDecimals,
        decimals,
        onKeyDown,
        onChange,
        value,
        multiline
    });

    // A line break travels as CRLF, so a textarea counts what it is sending
    // and not only what is written, because the service reads the value the
    // same way it is sent
    const countsLineBreaks = multiline && length !== "MAX";

    // A field that is only read cannot be scrolled, because it cannot be
    // focused, so it grows to display its whole value instead of cutting it.
    // The fields that render an element of their own keep the input they were
    // built with, because the value is not the only thing they display.
    const showsWholeValue = disabled && type === "text" && !InputProps;
    const asTextArea = multiline || showsWholeValue;

    return (
        <TextField size="small"
            sx={(asTextArea || length === "MAX")
                ? CustomStyleTextArea(lCode, showsWholeValue && !multiline)
                : CustomStyleTextField(lCode, false)}
            name={name}
            type={type}
            fullWidth
            label={standard ? '' : t(trans)}
            value={value === 0 ? 0 : (value || '')}
            disabled={disabled}
            readOnly={readOnly}
            id={id}
            onWheelCapture={e => {
                e.target.blur()
            }}
            className={((emptyFields && valueEmpty) || (!isValid)) ? "input-required" : ""}
            placeholder={placeholder}
            onClick={(e)=>{
                if(click)
                    click(e);
            }}
             onChange={type === "number"
                 ? handleNumberInputChange
                 : (countsLineBreaks ? handleMultilineChange : onChange)}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            onPaste={handlePaste}
            inputProps={{
                ...customInputProps,
                autoComplete: autoComplete,
                style: style
            }}
            InputProps={InputProps || {
                endAdornment: suffixText && (
                    <InputAdornment position="end">
                        <Box sx={CustomStyleSuffixText(disabled)}>
                            {suffixText}
                        </Box>
                    </InputAdornment>
                )
            }}
            multiline={asTextArea}
            minRows={asTextArea ? (multiline ? rows : 1) : undefined}
            maxRows={asTextArea && !showsWholeValue ? maxRows : undefined}
            required={required}
            error={(emptyFields && valueEmpty) || (!isValid)}
                   helperText={
                       showHelperTextAlways && helperText !== '' ? (
                               <Box
                                   component="span"
                                   className={`flex-end ${!isValid ? 'Mui-error' : ''}`}
                                   sx={CustomStyleFormHelperText(false, marginBottom)}
                                   disabled={disabled}
                               >
                                   {t(helperText)}
                               </Box>
                           ) :
                           !emptyFields && helperText !== '' && valueEmpty ? (
                                   <Box
                                       component="span"
                                       className={`${((emptyFields && valueEmpty) || (!isValid)) ? 'Mui-error' : ''}`}
                           sx={CustomStyleFormHelperText(false, marginBottom)}
                           disabled={disabled}
                       >
                           {t(helperText)}
                       </Box>
                   ) :
                   (
                       <CharacterCount
                           disabled={disabled}
                           count={decimals != null
                               ? (value?.toString()?.split('.')[0]?.length || 0)
                               : (countsLineBreaks ? getSentLength(value) : (value?.toString()?.length || 0))}
                           counterColor={counterColor}
                           helperText={emptyFields && valueEmpty ? t('requiredField') : !isValid ? t(isValidText) : ''}
                           max={length}
                           multiline={multiline}
                           showCounter={showCounter}
                           errorClass={((emptyFields && valueEmpty) || (!isValid)) ? 'Mui-error' : ''}
                           marginBottom={marginBottom}
                       />
                   )
            }
        />
    );
};


